import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../../lib/utils";

/**
 * Sticky in-tab anchor nav with scroll-spy (§3, §6). One scrolling dashboard,
 * no nested sub-tabs — this only moves the viewport, it never swaps content.
 *
 * Sticking works off the app's existing offset convention: NavigationTabs
 * publishes --sticky-tabs-offset, the identity header publishes its own height
 * as --pi-header-height, and this bar sits directly below both.
 */

export const ANCHORS = [
  { id: "pi-value", label: "Value" },
  { id: "pi-rhythm", label: "Rhythm" },
  { id: "pi-casino", label: "Casino" },
  { id: "pi-sports", label: "Sports" },
  { id: "pi-history", label: "History" },
];

export const NAV_STICKY_TOP = "calc(var(--sticky-tabs-offset, 99px) + var(--pi-header-height, 66px))";

/** The scroller these pages live in; falls back to the document. */
const scrollRoot = () => document.getElementById("main-scroll-container");

export function AnchorNav({ anchors = ANCHORS, resetKey, className }) {
  const [active, setActive] = useState(anchors[0]?.id);
  const navRef = useRef(null);
  // Suppresses spy updates while a click-driven smooth scroll is in flight, so
  // the highlight lands on the clicked section instead of flickering through
  // every section it passes on the way.
  const jumpingTo = useRef(null);

  // Publish this bar's height so section headings can set a scroll-margin that
  // clears the whole sticky stack, rather than a guessed pixel value.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;

    const publish = () => {
      document.documentElement.style.setProperty("--pi-nav-height", `${el.offsetHeight}px`);
    };
    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--pi-nav-height");
    };
  }, []);

  useEffect(() => {
    const el = navRef.current;
    const targets = anchors.map((anchor) => document.getElementById(anchor.id)).filter(Boolean);
    if (!el || !targets.length) return undefined;

    // Resolve the sticky stack's real height: the nav's own resolved top offset
    // plus its height. Anything above that line is behind the sticky chrome and
    // should not count as the current section.
    const stickyTop = parseFloat(window.getComputedStyle(el).top) || 0;
    const topInset = Math.round(stickyTop + el.offsetHeight + 8);

    const observer = new IntersectionObserver(
      (entries) => {
        if (jumpingTo.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      {
        root: scrollRoot(),
        // The deep bottom inset keeps exactly one section current, rather than
        // every section that happens to be below the fold.
        rootMargin: `-${topInset}px 0px -55% 0px`,
        threshold: 0,
      }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [anchors, resetKey]);

  const jump = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    jumpingTo.current = id;
    setActive(id);
    // Section headings carry a matching scroll-margin-top, so the browser lands
    // them clear of the sticky header and this bar.
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      jumpingTo.current = null;
    }, 700);
  };

  return (
    <nav
      ref={navRef}
      aria-label="Player insights sections"
      className={cn("sticky z-20 -mx-6 border-b bg-card px-6 py-2", className)}
      style={{ top: NAV_STICKY_TOP }}
    >
      <ul className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
        {anchors.map((anchor) => {
          const isActive = anchor.id === active;
          return (
            <li key={anchor.id}>
              <button
                type="button"
                onClick={() => jump(anchor.id)}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {anchor.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
